import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton } from '@/components/fx'
import { authApi } from '@/lib/api/auth'
import { ApiError } from '@/lib/api/errors'
import { useClientConfig } from './useClientConfig'
import {
  clearVerificationPause,
  useVerificationPause,
  verificationNotice,
} from './verificationGate'

/**
 * The unconfirmed-address banner, in the shell because the state is the
 * account's and not any one screen's.
 *
 * It no longer waits for a refusal to know where it stands: `GET /v1/config`
 * says whether the gate is on and, for an unconfirmed address, names the
 * instant uploads pause (see `verificationGate.ts` for the precedence).
 *
 * - **nothing** — the gate is off in this deployment, or the address is
 *   confirmed, or the config has not answered and nothing was refused.
 * - **reminder** — unconfirmed, still inside the window. With the deadline
 *   known it names the day ("… pausieren am 25.09."); without it, it promises
 *   none. Dismissible for the session either way.
 * - **paused** — past the gateway's deadline, or an upload or analysis was
 *   actually refused. It names what stopped *and* what did not: everything
 *   already stored stays readable, reviewable and exportable. Somebody who
 *   cannot reach their own work concludes the product is broken, and the
 *   gateway deliberately leaves those paths open.
 * - **linkBlocked** — only the phone upload link was refused. That gate has
 *   no grace window at all, so it is not a countdown and must not read like
 *   one.
 */
const dateFormat = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

function formatDate(value?: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : dateFormat.format(date)
}

export function VerificationBanner() {
  const { user } = useAuth()
  const config = useClientConfig()
  const pause = useVerificationPause()
  const [dismissed, setDismissed] = useState(false)

  const verified = user?.emailVerified
  useEffect(() => {
    // Somebody confirmed in another tab and the profile came back verified:
    // the recorded refusal is history, and the banner must not outlive it.
    if (verified) clearVerificationPause()
  }, [verified])

  const notice = verificationNotice({ user, config: config.data, pause, dismissed })
  if (!notice) return null

  const since = notice.kind === 'paused' ? formatDate(notice.pausedSince) : null
  const until = notice.kind === 'reminder' ? formatDate(notice.pausesAt) : null

  return (
    <FxAlert
      tone={notice.kind === 'reminder' ? 'warning' : 'danger'}
      title={
        notice.kind === 'reminder'
          ? until
            ? `Bitte bestätigen Sie Ihre E-Mail-Adresse bis zum ${until}`
            : 'Bitte bestätigen Sie Ihre E-Mail-Adresse'
          : notice.kind === 'linkBlocked'
            ? 'Für Upload-Links muss Ihre E-Mail-Adresse bestätigt sein'
            : since
              ? `Neue Uploads und Analysen sind seit dem ${since} pausiert`
              : 'Neue Uploads und Analysen sind pausiert'
      }
    >
      <p>
        {notice.kind === 'reminder' ? (
          <>
            Wir haben eine Bestätigung an <b>{notice.email}</b> geschickt.{' '}
            {until
              ? `Ohne Bestätigung pausieren wir neue Uploads und Analysen am ${until}.`
              : 'Solange die Adresse unbestätigt ist, können neue Uploads pausiert werden.'}
          </>
        ) : notice.kind === 'linkBlocked' ? (
          <>
            Ein Upload-Link lässt jede Person, die ihn hat, Dokumente in Ihr Konto legen. Bestätigen
            Sie dafür zuerst <b>{notice.email}</b>.
          </>
        ) : (
          <>
            Bestätigen Sie <b>{notice.email}</b>, um wieder hochzuladen und auszuwerten. Alles, was
            bereits gespeichert ist, bleibt vollständig nutzbar: Sie können Ihre Dokumente weiterhin
            öffnen, prüfen, korrigieren und exportieren.
          </>
        )}
      </p>
      <ResendRow dismissible={notice.kind === 'reminder'} onDismiss={() => setDismissed(true)} />
    </FxAlert>
  )
}

/**
 * The resend, with the rate limit treated as information rather than as an
 * error: five per hour is the gateway's protection for the address itself,
 * and somebody who clicks twice has done nothing wrong.
 */
function ResendRow({
  dismissible,
  onDismiss,
}: {
  dismissible: boolean
  onDismiss: () => void
}) {
  const resend = useMutation({
    mutationFn: () => authApi.resendVerification(),
    // A failure here is a state of the banner, not a toast: everything the
    // person needs to read is already in this box.
    onError: () => {},
  })

  const limited =
    resend.isError && ApiError.from(resend.error).code === 'resend_rate_limited'
  const failed = resend.isError && !limited

  return (
    <div className="flex flex-wrap items-center gap-4 pt-1">
      <FxButton
        variant="outlined"
        icon="mail"
        disabled={resend.isPending || resend.isSuccess || limited}
        onClick={() => resend.mutate()}
      >
        Bestätigung erneut senden
      </FxButton>
      {dismissible && (
        <FxButton variant="tonal" onClick={onDismiss}>
          Später
        </FxButton>
      )}
      <span aria-live="polite" className="text-small text-text-muted">
        {resend.isSuccess && 'Bestätigung verschickt. Bitte sehen Sie auch im Spam-Ordner nach.'}
        {limited &&
          'Wir haben in der letzten Stunde bereits fünf Bestätigungen verschickt. Bitte versuchen Sie es in einer Stunde noch einmal.'}
        {failed && 'Die Bestätigung konnte gerade nicht verschickt werden. Bitte später erneut versuchen.'}
      </span>
    </div>
  )
}
